import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const getBuyPrice = (stock) =>
  stock?.buy_price ?? stock?.purchase_item_details?.buy_price ?? '0.00';
const getSalePrice = (stock) =>
  stock?.sale_price ?? stock?.purchase_item_details?.sale_price ?? '0.00';

// Export stock data to PDF
export const exportStockToPDF = (stocks, searchTerm = '', productFilter = '') => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(26, 35, 126);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Stock Inventory Report', 15, 20);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 15, 28);
  doc.text(`Total Items: ${stocks.length}`, pageWidth - 15, 28, { align: 'right' });

  // Prepare table data
  const tableData = stocks.map((stock, index) => [
    index + 1,
    stock?.purchase_item_details?.product?.name || '--',
    stock?.purchase_item_details?.purchase?.supplier?.name || '--',
    getBuyPrice(stock),
    getSalePrice(stock),
    stock?.purchase_item_details?.expire_date || '--',
    stock?.quantity || '0',
    stock?.unit_details?.title || '--',
    stock?.currency_details?.title || '--',
    stock?.shop_details?.name || '--'
  ]);

  // Table configuration
  doc.autoTable({
    head: [['#', 'Product', 'Supplier', 'Buy / Purchase Price', 'Sale Price', 'Expire Date', 'Quantity', 'Unit', 'Currency', 'Shop']],
    body: tableData,
    startY: 45,
    theme: 'striped',
    headStyles: {
      fillColor: [26, 35, 126],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 3
    },
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 40 },
      2: { cellWidth: 32 },
      3: { cellWidth: 28 },
      4: { cellWidth: 25 },
      5: { cellWidth: 25 },
      6: { cellWidth: 20 },
      7: { cellWidth: 18 },
      8: { cellWidth: 20 },
      9: { cellWidth: 30 }
    },
    margin: { top: 45, left: 15, right: 15 }
  });

  // Footer
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(108, 117, 125);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 15, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
  }

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
  doc.save(`Stock_Report_${timestamp}.pdf`);
};

// Export stock data to Excel
export const exportStockToExcel = (stocks) => {
  const worksheetData = stocks.map((stock, index) => ({
    '#': index + 1,
    'Product Name': stock?.purchase_item_details?.product?.name || '--',
    'Supplier': stock?.purchase_item_details?.purchase?.supplier?.name || '--',
    'Buy / Purchase Price': getBuyPrice(stock),
    'Sale Price': getSalePrice(stock),
    'Expire Date': stock?.purchase_item_details?.expire_date || '--',
    'Available Quantity': stock?.quantity || '0',
    'Unit': stock?.unit_details?.title || '--',
    'Currency': stock?.currency_details?.title || '--',
    'Shop': stock?.shop_details?.name || '--',
    'Product ID': stock?.purchase_item_details?.product?.id || '',
    'Stock ID': stock?.id || ''
  }));

  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  
  // Set column widths
  const colWidths = [
    { wch: 5 },   // #
    { wch: 30 },  // Product Name
    { wch: 25 },  // Supplier
    { wch: 18 },  // Buy / Purchase Price
    { wch: 12 },  // Sale Price
    { wch: 12 },  // Expire Date
    { wch: 15 },  // Available Quantity
    { wch: 10 },  // Unit
    { wch: 12 },  // Currency
    { wch: 20 },  // Shop
    { wch: 12 },  // Product ID
    { wch: 10 }   // Stock ID
  ];
  worksheet['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Report');
  
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
  XLSX.writeFile(workbook, `Stock_Report_${timestamp}.xlsx`);
};

// Export stock data to CSV
export const exportStockToCSV = (stocks) => {
  const csvData = stocks.map((stock, index) => ({
    '#': index + 1,
    'Product Name': stock?.purchase_item_details?.product?.name || '--',
    'Supplier': stock?.purchase_item_details?.purchase?.supplier?.name || '--',
    'Buy / Purchase Price': getBuyPrice(stock),
    'Sale Price': getSalePrice(stock),
    'Expire Date': stock?.purchase_item_details?.expire_date || '--',
    'Available Quantity': stock?.quantity || '0',
    'Unit': stock?.unit_details?.title || '--',
    'Currency': stock?.currency_details?.title || '--',
    'Shop': stock?.shop_details?.name || '--'
  }));

  const worksheet = XLSX.utils.json_to_sheet(csvData);
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
  saveAs(blob, `Stock_Report_${timestamp}.csv`);
};

// Print stock list
export const printStockList = (stocks) => {
  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Stock Inventory Report</title>
        <meta charset="utf-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #2c3e50;
            background: white;
            padding: 20px;
          }
          .header { 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-align: center;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 20px;
          }
          .header h1 { 
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 8px;
          }
          .header p { 
            font-size: 14px;
            opacity: 0.9;
          }
          .info-bar {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 6px;
            margin-bottom: 20px;
            display: flex;
            justify-content: space-between;
            border-left: 4px solid #667eea;
          }
          table { 
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          th { 
            background: linear-gradient(90deg, #667eea, #764ba2);
            color: white;
            font-weight: 600;
            padding: 12px 8px;
            text-align: left;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          td { 
            padding: 10px 8px;
            border-bottom: 1px solid #e9ecef;
            font-size: 11px;
            vertical-align: middle;
          }
          tr:nth-child(even) { 
            background: #f8f9fa;
          }
          tr:hover { 
            background: #e3f2fd;
          }
          .footer { 
            margin-top: 30px;
            padding: 15px;
            background: #f8f9fa;
            border-radius: 6px;
            text-align: center;
            color: #6c757d;
            font-size: 10px;
          }
          @media print {
            body { padding: 10px; }
            .header { margin-bottom: 15px; }
            .info-bar { margin-bottom: 15px; }
          }
          @page {
            margin: 0.5in;
            size: A4 landscape;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📦 Stock Inventory Report</h1>
          <p>Generated on ${new Date().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}</p>
        </div>
        
        <div class="info-bar">
          <div><strong>Total Items:</strong> ${stocks.length}</div>
          <div><strong>Report Type:</strong> Complete Stock List</div>
          <div><strong>Status:</strong> Current Inventory</div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 5%">#</th>
              <th style="width: 25%">Product Name</th>
              <th style="width: 15%">Supplier</th>
              <th style="width: 10%">Buy / Purchase Price</th>
              <th style="width: 10%">Sale Price</th>
              <th style="width: 10%">Expire Date</th>
              <th style="width: 10%">Quantity</th>
              <th style="width: 8%">Unit</th>
              <th style="width: 8%">Currency</th>
              <th style="width: 15%">Shop</th>
            </tr>
          </thead>
          <tbody>
            ${stocks.map((stock, index) => `
              <tr>
                <td>${index + 1}</td>
                <td><strong>${stock?.purchase_item_details?.product?.name || '--'}</strong></td>
                <td>${stock?.purchase_item_details?.purchase?.supplier?.name || '--'}</td>
                <td style="text-align: right; font-weight: 600;">${getBuyPrice(stock)}</td>
                <td style="text-align: right; font-weight: 600;">${getSalePrice(stock)}</td>
                <td>${stock?.purchase_item_details?.expire_date || '--'}</td>
                <td style="text-align: center; font-weight: 600;">${stock?.quantity || '0'}</td>
                <td>${stock?.unit_details?.title || '--'}</td>
                <td>${stock?.currency_details?.title || '--'}</td>
                <td>${stock?.shop_details?.name || '--'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p><strong>📦 Stock Inventory Report</strong> • Generated by Inventory Management System</p>
          <p>Report generated on ${new Date().toLocaleString()}</p>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes');
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  } else {
    alert('Please allow popups to print the report');
  }

  return printWindow;
};
